
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { getVersionInfo } from '@/config/version';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class MobileErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const versionInfo = getVersionInfo();
    
    console.error('Mobile Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      versionInfo,
      timestamp: new Date().toISOString()
    });

    this.setState({
      error,
      errorInfo
    });

    // If on mobile, show additional debugging info
    if (versionInfo.isMobile) {
      console.error('Mobile-specific error details:', {
        viewport: versionInfo.viewport,
        userAgent: versionInfo.userAgent,
        touchSupport: 'ontouchstart' in window,
        devicePixelRatio: window.devicePixelRatio
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const versionInfo = getVersionInfo();
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-red-600 mb-4">
              {versionInfo.isMobile ? 'Mobile Error Detected' : 'Error Detected'}
            </h2>
            <div className="space-y-3 text-sm">
              <div>
                <strong>Version:</strong> {versionInfo.version}
              </div>
              <div>
                <strong>Device:</strong> {versionInfo.isMobile ? 'Mobile' : 'Desktop'}
              </div>
              <div>
                <strong>Build:</strong> {new Date(versionInfo.buildTimestamp).toLocaleString()}
              </div>
              <div>
                <strong>Error:</strong> {this.state.error?.message}
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default MobileErrorBoundary;
